import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, PaperPlaneTilt, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { downloadBlob } from "../lib/auditDownload";
import MasterIntakeQuestion, { masterApplies } from "../components/MasterIntakeQuestion";
import { Button } from "../components/ui/button";

const emptyStart = { full_name: "", phone: "", email: "", payer: "" };

export default function ClientIntakePage() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const clientId = cid && cid !== "new" ? cid : "";
  const [payload, setPayload] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [start, setStart] = useState(emptyStart);
  const [starting, setStarting] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const r = await api.get(`/clients/${clientId}/master-intake`);
      setPayload(r.data);
      setAnswers(r.data.answers || {});
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/clients/intake", { replace: true });
    }
  }, [clientId, navigate]);

  useEffect(() => { load(); }, [load]);

  const questions = useMemo(() => (payload?.questions || []).filter((q) => masterApplies(q, answers)), [payload, answers]);
  const q = questions[Math.min(idx, Math.max(questions.length - 1, 0))];

  const saveCurrent = async (nextIdx) => {
    if (!q) return;
    setSaving(true);
    try {
      const value = answers[q.key];
      await api.put(`/clients/${clientId}/master-intake/answers`, { answers: { [q.key]: value } });
      await load();
      setIdx(nextIdx);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const startClient = async () => {
    if (!start.full_name.trim()) return;
    setStarting(true);
    try {
      const r = await api.post("/clients", { intake: { full_name: start.full_name.trim(), phone: start.phone.trim(), email: start.email.trim(), payer: start.payer.trim() } });
      const seed = { q1: start.full_name.trim() };
      if (start.phone.trim()) seed.q12 = start.phone.trim();
      if (start.email.trim()) seed.q13 = start.email.trim();
      if (start.payer.trim()) seed.q23 = [start.payer.trim()];
      await api.put(`/clients/${r.data.id}/master-intake/answers`, { answers: seed });
      navigate(`/clients/intake/${r.data.id}`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setStarting(false);
    }
  };

  const sendLink = async () => {
    try {
      const r = await api.post(`/clients/${clientId}/intake-link`, { send: false });
      await navigator.clipboard.writeText(r.data.url);
      toast.success("Client intake link copied");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!clientId) {
    return (
      <div className="mx-auto max-w-2xl p-6" data-testid="master-intake-start">
        <button onClick={() => navigate("/clients")} className="mb-6 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="mi-back-clients">
          <ArrowLeft size={14} /> Clients
        </button>
        <div className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-400">Master Intake v2</p>
          <h1 className="mt-2 font-heading text-4xl font-semibold tracking-tight text-stone-900">Start a new client</h1>
          <p className="mt-3 text-base leading-relaxed text-stone-600">The intake now writes one Client Profile. That profile fills the information sheet, care plan, agreement, medication list, sharing authorization, receipt, and home-safety review.</p>
          <div className="mt-6 grid gap-4">
            <input value={start.full_name} onChange={(e) => setStart({ ...start, full_name: e.target.value })} placeholder="Client's full legal name" className="h-12 rounded-xl border border-stone-300 px-4 text-lg" data-testid="mi-start-name" />
            <input value={start.phone} onChange={(e) => setStart({ ...start, phone: e.target.value })} placeholder="Phone" className="h-12 rounded-xl border border-stone-300 px-4" data-testid="mi-start-phone" />
            <input value={start.email} onChange={(e) => setStart({ ...start, email: e.target.value })} placeholder="Email, if any" className="h-12 rounded-xl border border-stone-300 px-4" data-testid="mi-start-email" />
            <select value={start.payer} onChange={(e) => setStart({ ...start, payer: e.target.value })} className="h-12 rounded-xl border border-stone-300 bg-white px-4" data-testid="mi-start-payer">
              <option value="">How will care be paid for?</option>
              {['CHC MCO', 'Private pay', 'Veterans benefit', 'Long-term care insurance', 'Other coverage', 'Unknown'].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <Button onClick={startClient} disabled={!start.full_name.trim() || starting} className="mt-6 h-12 rounded-full bg-[#4A235A] px-7 text-white" data-testid="mi-start-create">
            {starting ? "Creating…" : "Create client & begin"}
          </Button>
        </div>
      </div>
    );
  }

  if (!payload) return <div className="flex justify-center py-24" data-testid="mi-loading"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;

  const readiness = payload.readiness || {};
  const done = idx >= questions.length;

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6" data-testid="master-intake-page">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/clients")} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="mi-back">
            <ArrowLeft size={14} /> Clients
          </button>
          <Link to={`/clients/${clientId}`} className="text-sm font-medium text-[#6A3E97] hover:underline" data-testid="mi-full-record">Open client record →</Link>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={sendLink} className="rounded-full" data-testid="mi-send-link"><PaperPlaneTilt size={15} className="mr-1" /> Copy client link</Button>
          <Button variant="outline" onClick={() => downloadBlob(`/clients/${clientId}/intake-form.pdf`, "master-intake.pdf")} className="rounded-full" data-testid="mi-print-intake"><Printer size={15} className="mr-1" /> Print intake</Button>
          <Button variant="outline" onClick={() => downloadBlob(`/clients/${clientId}/intake-review.pdf`, "master-intake-review.pdf")} className="rounded-full" data-testid="mi-print-checklist"><Printer size={15} className="mr-1" /> Print checklist</Button>
          <Link to={`/clients/${clientId}/paperwork`} className="inline-flex h-10 items-center rounded-full border border-stone-300 px-4 text-sm font-medium" data-testid="mi-paperwork">Paperwork →</Link>
        </div>
      </div>

      <div className="mb-5 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="mi-status-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-stone-900">Master Intake — {payload.client_name}</h1>
            <p className="mt-1 text-sm text-stone-500">Every answer updates the Client Profile and the forms it feeds.</p>
          </div>
          <div className="flex gap-2 text-xs font-bold">
            <span className="rounded-full bg-stone-100 px-3 py-1 text-stone-600" data-testid="mi-answered-count">{readiness.answered || 0}/{readiness.applicable || 0} answered</span>
            <span className={`rounded-full px-3 py-1 ${readiness.agreement_ready ? "bg-[#2E7D32]/10 text-[#2E7D32]" : "bg-[#B7791F]/10 text-[#8a5d14]"}`} data-testid="mi-agreement-ready">Agreement {readiness.agreement_ready ? "ready" : "not ready"}</span>
            <span className={`rounded-full px-3 py-1 ${readiness.careplan_ready ? "bg-[#2E7D32]/10 text-[#2E7D32]" : "bg-[#B7791F]/10 text-[#8a5d14]"}`} data-testid="mi-careplan-ready">Care plan {readiness.careplan_ready ? "ready" : "not ready"}</span>
            <button type="button" onClick={() => setShowChecklist((v) => !v)} className="rounded-full bg-stone-900 px-3 py-1 text-white" data-testid="mi-toggle-checklist">
              Checklist {payload.checklist?.counts?.ready || 0}/{payload.checklist?.items?.length || 0}
            </button>
          </div>
        </div>
      </div>

      {showChecklist && (
        <div className="mb-5 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="mi-review-checklist">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-heading text-xl font-semibold text-stone-900">Office review checklist</h2>
              <p className="text-sm text-stone-500">Ready {payload.checklist?.counts?.ready || 0} · Review {payload.checklist?.counts?.review || 0} · Missing {payload.checklist?.counts?.missing || 0}</p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${payload.checklist?.can_send_for_signature ? "bg-[#2E7D32]/10 text-[#2E7D32]" : "bg-[#B7791F]/10 text-[#8a5d14]"}`} data-testid="mi-checklist-result">
              {payload.checklist?.can_send_for_signature ? "Ready for final document review" : "Not ready to send for signature"}
            </span>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {(payload.checklist?.items || []).map((item, i) => (
              <div key={`${item.section}-${i}`} className="rounded-xl border border-stone-200 p-3" data-testid={`mi-check-${item.status}-${i}`}>
                <div className="flex items-start gap-3">
                  <span className={`mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${item.status === "ready" ? "bg-[#2E7D32]/10 text-[#2E7D32]" : item.status === "review" ? "bg-[#B7791F]/10 text-[#8a5d14]" : "bg-[#C0392B]/10 text-[#C0392B]"}`}>{item.status}</span>
                  <div>
                    <p className="text-sm font-bold text-stone-800">{item.label}</p>
                    <p className="mt-1 text-xs leading-relaxed text-stone-500">{item.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      {done ? (
        <div className="rounded-3xl border border-stone-200 bg-white p-10 text-center shadow-sm" data-testid="mi-complete">
          <h2 className="font-heading text-3xl font-semibold text-stone-900">Intake complete for review</h2>
          <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-stone-600">The Client Profile has been updated. Review the generated documents before the client signs.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to={`/clients/${clientId}/careplan-review`} className="rounded-full bg-[#4A235A] px-5 py-3 text-sm font-semibold text-white" data-testid="mi-review-careplan">Review Care Plan</Link>
            <Link to={`/clients/${clientId}/service-agreement`} className="rounded-full border border-stone-300 px-5 py-3 text-sm font-semibold" data-testid="mi-review-agreement">Review Agreement</Link>
            <Link to={`/clients/${clientId}/paperwork`} className="rounded-full border border-stone-300 px-5 py-3 text-sm font-semibold" data-testid="mi-open-paperwork">Open Paperwork</Link>
          </div>
          <button onClick={() => setIdx(0)} className="mt-5 text-sm text-stone-500 underline" data-testid="mi-review-answers">Review answers</button>
        </div>
      ) : q ? (
        <div className="rounded-3xl border border-stone-200 bg-white p-7 shadow-sm" data-testid="mi-question-card">
          <MasterIntakeQuestion q={q} value={answers[q.key]} onChange={(v) => setAnswers((a) => ({ ...a, [q.key]: v }))} testidPrefix="mi" />
          <div className="mt-7 flex items-center justify-between">
            <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0 || saving} className="text-sm text-stone-400 hover:text-stone-700 disabled:opacity-0" data-testid="mi-prev">← Back</button>
            <div className="flex items-center gap-3">
              <span className="text-sm text-stone-400" data-testid="mi-progress">{idx + 1} of {questions.length}</span>
              <Button onClick={() => saveCurrent(idx + 1)} disabled={saving} className="rounded-full bg-[#4A235A] px-7 text-white" data-testid="mi-next">
                {saving ? "Saving…" : idx >= questions.length - 1 ? "Finish" : "Save & next"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
